<script lang="ts" setup>
import { ChooseProjectDropdown, EditQueueModal } from '@/components';
import { ProjectModel } from '@/models';
import {
  PopoverPositionEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmModal,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPopover,
  TirPmRadio,
  TirPmScrollbar,
  TirPmSkeletonLoader,
  TirPmSvg,
} from 'tir-components';
import { TirPmSearchInput } from 'tir-pm-search-input';
import { computed, onMounted, ref, watch } from 'vue';
import {
  BaseProjectResponse,
  GetListByProjectIdRequest,
  PageRequest,
  ProjectNamespaceListItem,
  ProjectNamespaceListRequest,
  ProjectSearchRequest,
  QueueModel,
} from './models';
import { AdapterService } from './adapter-service';
import { debounce } from 'tir-pm-debounce';
import { useRouter } from 'vue-router';
import { ListBulletIcon, QuestionMarkCircleIcon } from 'tir-style-system/icons/outline';
import TaskMoveRequestModel from '../task-drawer/models/TaskMoveRequestModel';
import { TirPmFoldPanel } from 'tir-pm-fold-panel';
import GetListStatusRequestModel from './models/GetListStatusRequestModel';
import GetListStatusResponseItemModel from './models/GetListStatusResponseItemModel';
import { StatusParameter } from './components';

/**Маршруты */
const router = useRouter();
/**API сервис */
const apiService = new AdapterService();

/**Параметры */
const props = defineProps({
  /**id проекта */
  projectId: { type: Number, required: true },
  /**Id задачи */
  taskId: { type: Number },
  /**есть ли родительская задача */
  isParentalTask: { type: Boolean },
  /**Прикрепленные очереди */
  attachedQueues: { type: Boolean },
  /**наличие подзадач */
  isSubtasks: { type: Boolean },
});
/**События */
const emit = defineEmits<{
  showSettingsTask: [value: boolean];
  /**Событие переноса тикета */
  transferTask: [];
}>();

/**Открыто ли окно */
const visible = ref(false);
/**Выбранный проект */
const project = ref<ProjectModel>();
/**Поисковой запрос */
const search = ref('');
/**Поиск по НС */
const searchNamespace = ref('');
/**Поиск по очередям */
const searchQueue = ref('');
/**Количество очередей */
const queuesCount = ref();
/**Список очередей */
const queues = ref<QueueModel[]>();
/**Происходит загрузка */
const isLoadingId = ref();
/**Загрузка очередей */
const isLoadingQueueId = ref();
/**ID задачи */
const taskId = ref<number>();
/**Редактируемая очередь */
const editQueue = ref();
/**Список рабочих областей */
const namespaces = ref<ProjectNamespaceListItem[]>([]);
/**id раскрытого НС */
const openedNamespaceId = ref<string>();
/**Общ. кол-во НС */
const namespaceTotalCount = ref<number>();
/**Выбранная очередь */
const selectedQueue = ref<QueueModel>();
/**Список статусов выбранной очереди */
const statusesList = ref<GetListStatusResponseItemModel[]>();
/**Загрузка статусов */
const isLoadingStatus = ref();

/**После рендера компонента */
onMounted(async () => {
  await getNamespace();
});

/**Пагинация */
const pageRequest = computed(() => ({
  Skip: 0,
  Take: 15,
}));

/**Переключение видимости окна */
const toggle = (_visible: boolean, _taskId?: number) => {
  visible.value = _visible;
  taskId.value = _taskId;
  openedNamespaceId.value = null;
  selectedQueue.value = null;

  if (!_visible) {
    resetQueues();
    searchNamespace.value = '';
    searchQueue.value = '';
    project.value = undefined;
  }
};
/**Переключение видимости панели НС */
const toggleVisiblePanelNamespace = async (namespaceId: string) => {
  openedNamespaceId.value = openedNamespaceId.value === namespaceId ? null : namespaceId;
  if (openedNamespaceId.value === namespaceId) await getQueues();
};
/**Получить рабочие области */
const getNamespace = async () => {
  const response = await apiService.getNamespaces(
    new ProjectNamespaceListRequest({
      ProjectId: props.projectId,
      Search: searchNamespace.value,
      Page: new PageRequest(pageRequest.value),
    }),
  );
  if (response.IsSuccess) {
    namespaces.value = response.Value.Items;
  }
  isLoadingId.value = null;
};
/**Получить список следующих рабочих областей */
const getNextNamespace = async () => {
  const response = await apiService.getNamespaces(
    new ProjectNamespaceListRequest({
      ProjectId: props.projectId,
      Search: searchNamespace.value,
      Page: new PageRequest({
        Skip: namespaces.value.length,
        Take: pageRequest.value.Take,
      }),
    }),
  );
  if (response.IsSuccess) {
    namespaces.value.push(...response.Value.Items);
    namespaceTotalCount.value = response.Value.Page.Count;
  }
  isLoadingId.value = null;
};
/**Отсроченное получение проекта */
const debounceSearchNamespaces = debounce(getNamespace, 500);

/**Получение очередей */
async function getQueues() {
  if (!openedNamespaceId.value) return;
  isLoadingQueueId.value = openedNamespaceId.value;
  const response = await apiService.getQueues(
    new GetListByProjectIdRequest({
      ProjectId: props.projectId,
      NamespaceId: openedNamespaceId.value,
      Search: searchQueue.value,
      Page: pageRequest.value,
    }),
  );
  if (response.IsSuccess) {
    queues.value = response.Items;
    queuesCount.value = response.Count;
  }
  isLoadingQueueId.value = null;
}
/**Отсроченное получение проекта */
const debounceSearchQueue = debounce(getQueues, 500);
/**Сброс текущих очередей */
const resetQueues = () => {
  queues.value = [];
  queuesCount.value = undefined;
};
/**Перенос тикета */
const transferTask = async (queue: QueueModel, _statusId: number) => {
  let res = await apiService.taskMove(
    new TaskMoveRequestModel({
      TaskId: props.taskId,
      NewQueueTaskId: selectedQueue.value.Id,
      StatusId: _statusId,
    }),
  );
  if (res.IsSuccess) {
    router.push({
      name: 'queue',
      params: {
        projectId: props.projectId,
        queueId: selectedQueue.value.Id,
        namespaceId: queue.NamespaceId,
      },
    });
    addNotification('Успешный перенос тикета', false, 5000);
    visible.value = false;
    emit('transferTask');
  } else {
    addNotification(res.ErrorMessage, true, 5000);
  }
};
/**При скроле спика нс */
const onScrollNamespaceList = async (data: Event) => {
  const el = data.target as HTMLElement;
  const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

  if (reachedBottom && namespaceTotalCount.value !== namespaces.value.length) {
    await getNextNamespace();
  }
};

/**Получить статусы очереди */
const getStatusesQueue = async () => {
  isLoadingStatus.value = true;
  const response = await apiService.getStatusesListDetail(
    new GetListStatusRequestModel({
      QueueTasksId: [selectedQueue.value.Id],
    }),
  );
  if (response.IsSuccess) {
    statusesList.value = response.Value.Items;
  }
  isLoadingStatus.value = false;
};
/**Открытая панель НС */
const openPanelNamespace = (namespaceId: string) => {
  return openedNamespaceId.value === namespaceId;
};
/**Выбрать очередь */
const selectQueue = (queue: QueueModel) => {
  selectedQueue.value = queue;
};
/**Закрыть окно редактирования очереди */
const closeEditQueueModal = async () => {
  editQueue.value = null;
  resetQueues();
  await getQueues();
};

/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};

/**Код для использования по ref */
defineExpose({ toggle });
</script>
<template>
  <TirPmModal
    ref="$modal"
    classes="task-transfer-modal_window"
    is-close-overlay
    :title="`Перенос тикета #${props.taskId}`"
    :is-with-back="false"
    :is-visible="visible"
    @close="toggle(false)"
  >
    <template #content>
      <div class="task-transfer-modal">
        <div class="task-transfer-modal_queues">
          <div class="task-transfer-modal_queues_header">
            <TirPmSearchInput
              v-if="!openedNamespaceId"
              class="task-transfer-modal_search"
              v-model="searchNamespace"
              placeholder=""
              label="Поиск по рабочим областям"
              :is-with-popup="false"
              @update:model-value="debounceSearchNamespaces"
            />
            <TirPmSearchInput
              v-else
              class="task-transfer-modal_search"
              v-model="searchQueue"
              placeholder=""
              label="Поиск по очередям"
              :is-with-popup="false"
              @update:model-value="debounceSearchQueue"
            />
          </div>
          <TirPmScrollbar
            class="task-transfer-modal_scrollbar"
            showOnHover
            @onScroll="onScrollNamespaceList"
          >
            <div class="task-transfer-modal_panel-namespaces-block">
              <div v-for="namespace in namespaces" :key="namespace.Id">
                <TirPmFoldPanel
                  :isVisible="openPanelNamespace(namespace.Id)"
                  @toggle="toggleVisiblePanelNamespace(namespace.Id)"
                  class="task-transfer-modal_panel-namespaces task-transfer-modal_panel"
                >
                  <template #header>
                    <div class="task-transfer-modal_project-info">
                      <TirPmSvg
                        class="task-transfer-modal_namespace-icon"
                        :url="namespace.IconUrl"
                        :color="namespace.Color"
                      />
                      <div
                        class="task-transfer-modal_project-name"
                        :class="{ 'fold-panel--active': openPanelNamespace(namespace.Id) }"
                      >
                        {{ namespace.Name }}
                      </div>
                    </div>
                  </template>
                  <template #content>
                    <div
                      v-if="isLoadingQueueId === namespace.Id"
                      class="task-transfer-modal_panel-skeleton"
                    >
                      <TirPmSkeletonLoader width="100%" height="2.5rem" borderRadius="62.4375rem" />
                      <TirPmSkeletonLoader width="100%" height="2.5rem" borderRadius="62.4375rem" />
                    </div>
                    <div
                      v-else
                      v-for="queue in queues"
                      :key="queue.Id"
                      class="task-transfer-modal_queue-info"
                    >
                      <div class="task-transfer-modal_queue-info--radio-block">
                        <TirPmRadio
                          :value="1"
                          :model-value="selectedQueue?.Id === queue.Id ? 1 : 0"
                          @update:modelValue="selectQueue(queue)"
                        />
                        <ListBulletIcon class="task-transfer-modal_icon" />
                      </div>
                      <div>{{ queue.Name }}</div>
                    </div>
                    <div v-if="!queues?.length && !isLoadingQueueId">
                      <div class="task-transfer-modal_no-find-data no-find-queues">
                        <TirPmIconView :size="24" :isBordered="false">
                          <template #mainSlot>
                            <QuestionMarkCircleIcon />
                          </template>
                        </TirPmIconView>
                        <div>Данные не найдены</div>
                      </div>
                    </div>
                  </template>
                </TirPmFoldPanel>
              </div>
              <div v-if="!namespaces.length && !isLoadingId">
                <div class="task-transfer-modal_no-find-data">
                  <TirPmIconView :size="24" :isBordered="false">
                    <template #mainSlot>
                      <QuestionMarkCircleIcon />
                    </template>
                  </TirPmIconView>
                  <div>Данные не найдены</div>
                </div>
              </div>
            </div>
          </TirPmScrollbar>
        </div>
      </div>
    </template>
    <template #footer>
      <TirPmPopover
        :customPosition="{ left: '0rem', top: 'unset' }"
        isNoTeleport
        :isDisabled="!selectedQueue"
        class="task-transfer-modal_popover"
      >
        <TirPmButton
          :variant="TirPmButtonVariantEnum.PrimaryState"
          :size="TirPmButtonSizeEnum.Regular"
          :isDisabled="!selectedQueue"
          @click="getStatusesQueue"
        >
          Выбрать статус и завершить перенос
        </TirPmButton>
        <template #content>
          <TirPmScrollbar class="task-transfer-modal_scrollbar-status-list" showOnHover>
            <div v-for="status in statusesList" :key="status.Id">
              <StatusParameter
                :options="status"
                @selectStatus="transferTask(selectedQueue, status.Id)"
              />
            </div>
          </TirPmScrollbar>
        </template>
      </TirPmPopover>
    </template>
  </TirPmModal>
  <EditQueueModal :queue="editQueue" @close="closeEditQueueModal" />
</template>
<style lang="scss">
.task-transfer-modal {
  &_namespace-icon {
    font-size: 1.5rem;
  }
  height: 100%;
  &_search {
    width: 100% !important;
  }
  &_popover {
    height: 12rem;
  }
  &_scrollbar-status-list {
    // height: 14.1875rem;
    max-height: 15rem;
  }
  &_window.modal {
    width: 76.25rem;
    height: 48.4375rem;
    max-width: 100%;
    .modal__content {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      padding: 2.5rem;
    }
  }
  &_no-find-data {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-secondary, #686c73);
    padding: 0.5rem 0 0.5rem 1.5rem;
  }
  .sidebar {
    background-color: unset; //TODO надо учесть светлую и темную тему
  }
  &_panel {
    overflow: hidden;
  }
  &_panel-skeleton {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.75rem;
  }
  &_icon {
    width: 1.5rem;
    height: 1.5rem;
  }
  &_panel-namespaces-block {
    margin-bottom: 0.75rem;
  }
  &_queue-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0 0.5rem 3rem;
    &--radio-block {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }
  &_scrollbar {
    max-height: 32rem;
  }
  &_projects {
    border-bottom: 0.0625rem solid var(--neutral-20, #d4d5d7);
  }
  &_project-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  &_project-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 1rem;
    font-weight: 500;
    color: var(--text-primary, #272d37);
  }
  &_sidebar {
    display: flex;
    justify-content: center;
    width: 100%;
    max-width: 18.75rem;
    height: 100%;
    min-height: 40.125rem;
    border: 0.0625rem solid var(--neutral-10);
    border-radius: 0.5rem;
    padding: 0.75rem;
  }
  &_scroll {
    margin-top: 2.25rem;
    width: calc(100% + 0.75rem);
    padding-right: 0.75rem;
  }
  &_queues {
    height: 100%;
    &_header {
      margin-bottom: 1.75rem;
      &_total {
        color: var(--text-primary);
        font-size: 0.875rem;
        font-weight: 500;
      }
    }
    &_list {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 35.625rem;
      gap: 0.75rem;
    }
  }
}
.fold-panel--active {
  color: var(--text-info, #2a77ef);
}
.task-transfer-modal_queue-info {
  &:hover {
    background-color: var(--neutral-translucent-5, #272d370d);
  }
}
.no-find-queues {
  padding: 0.5rem 0 0.5rem 1.5rem;
}
.task-transfer-modal_panel {
  .tir-pm-fold-panel__header {
    padding: 0.75rem 0;

    &:hover {
      background-color: var(--neutral-translucent-5, #272d370d);
    }
  }
}
.task-transfer-modal_panel-namespaces {
  .tir-pm-fold-panel__header {
    padding: 0.5rem 0 0.5rem 0.75rem;
  }
}
.task-transfer-modal_window .modal__content-scroll {
  height: 100%;
}

.task-transfer-modal_popover .tir-popover_container .tir-popover_content {
  bottom: 13rem;
}
</style>
